/*
 * Copyright (C) 2019 Toshiba Corporation
 * SPDX-License-Identifier: Apache-2.0
 */

import { InteractionEvent, Point } from "pixi.js";
import { DBase } from "./d-base";
import { UtilGestureMode } from "./util/util-gesture-mode";
import { UtilGestureModifier } from "./util/util-gesture-modifier";
import { DThemeViewGesture, DViewGesture, DViewGestureOptions } from "./d-view-gesture";
import { DViewParent } from "./d-view-parent";
import { DViewTarget, DViewToTarget } from "./d-view-to-target";
import { toEnum } from "./util/to-enum";
import { UtilGesture } from "./util/util-gesture";
import { DViewConstraint } from "./d-view-constraint";

export class DViewGestureImpl implements DViewGesture {
	protected _owner: DBase;
	protected _parent: DViewParent;
	protected _constraint: DViewConstraint;
	protected _gestureUtil?: UtilGesture<DBase>;

	constructor(
		owner: DBase,
		toTarget: DViewToTarget,
		parent: DViewParent,
		constraint: DViewConstraint,
		theme: DThemeViewGesture,
		options?: DViewGestureOptions
	) {
		this._owner = owner;
		this._parent = parent;
		this._constraint = constraint;

		const mode = toEnum(options?.mode ?? theme.getGestureMode(), UtilGestureMode);
		const modifier = toEnum(
			options?.modifier ?? theme.getGestureModifier(),
			UtilGestureModifier
		);
		if (mode === UtilGestureMode.ON || mode === UtilGestureMode.TOUCH) {
			const work = new Point();
			this._gestureUtil = new UtilGesture<DBase>({
				touch: mode === UtilGestureMode.TOUCH,
				modifier,
				checker: options && options.checker,
				easing: {
					duration: options?.duration
				},
				on: {
					start: (target): void => {
						this.onStart(target);
					},
					move: (
						target: DBase,
						dx: number,
						dy: number,
						x: number,
						y: number,
						ds: number
					): void => {
						this.onGestureMove(toTarget(owner), dx, dy, x, y, ds, work);
					},
					easing: {
						end: (target): void => {
							this.onEnd(target);
						}
					},
					stop: (target): void => {
						this.onStop(target);
					},
					tap: (target, e): void => {
						this.onTap(target, e);
					},
					longpress: (target, e): void => {
						this.onLongPress(target, e);
					}
				}
			});
		}
	}

	protected onStart(target: DBase): void {
		const parent = this._parent;
		parent.stop();
		parent.emit("gesturestart", target, parent);
	}

	protected onEnd(target: DBase): void {
		const parent = this._parent;
		parent.emit("gestureend", target, parent);
	}

	protected onStop(target: DBase): void {
		const parent = this._parent;
		parent.emit("gesturestop", target, parent);
	}

	protected onTap(target: DBase, e: InteractionEvent): void {
		const parent = this._parent;
		parent.emit("gesturetap", target, e, parent);
	}

	protected onLongPress(target: DBase, e: InteractionEvent): void {
		const parent = this._parent;
		parent.emit("gesturelongpress", target, e, parent);
	}

	protected onGestureMove(
		target: DViewTarget | null,
		dx: number,
		dy: number,
		x: number,
		y: number,
		ds: number,
		work: Point
	): void {
		if (target != null) {
			// Scale
			const parent = this._parent;
			const oldScale = target.scale;
			const oldScaleX = oldScale.x;
			const oldScaleY = oldScale.y;
			let newScaleX = parent.toNormalizedScale(oldScaleX * ds);
			let newScaleY = parent.toNormalizedScale(oldScaleY * ds);

			const scaleRatioX = newScaleX / oldScaleX;
			const scaleRatioY = newScaleY / oldScaleY;
			const scaleRatio =
				ds < 1 ? Math.max(scaleRatioX, scaleRatioY) : Math.min(scaleRatioX, scaleRatioY);
			newScaleX = scaleRatio * oldScaleX;
			newScaleY = scaleRatio * oldScaleY;

			// Position
			const targetParent = target.parent;
			if (targetParent != null) {
				targetParent.toLocal(work.set(x, y), undefined, work);
				x = work.x;
				y = work.y;
			}
			const cx = x - dx;
			const cy = y - dy;
			const position = target.position;
			const newX = (position.x - cx) * scaleRatio + x;
			const newY = (position.y - cy) * scaleRatio + y;

			// Update
			this._constraint(target, newX, newY, newScaleX, newScaleY);
		}
	}

	stop(): void {
		this._gestureUtil?.stop(this._owner);
	}

	onDown(e: InteractionEvent): void {
		this._gestureUtil?.onDown(this._owner, e);
	}
}
